'use client';
import Icon from '@/components/ui/AppIcon';

const clients = [
  { name: 'Microsoft', color: '#00A4EF', emoji: '⊞' },
  { name: 'Google', color: '#4285F4', emoji: 'G' },
  { name: 'Amazon', color: '#FF9900', emoji: 'a' },
  { name: 'Stripe', color: '#635BFF', emoji: 'S' },
  { name: 'Shopify', color: '#96BF48', emoji: '🛒' },
  { name: 'Airbnb', color: '#FF5A5F', emoji: '◆' },
  { name: 'Netflix', color: '#E50914', emoji: 'N' },
  { name: 'Spotify', color: '#1DB954', emoji: '♫' },
  { name: 'Figma', color: '#F24E1E', emoji: '◈' },
  { name: 'Notion', color: '#FFFFFF', emoji: '◻' },
  { name: 'Vercel', color: '#FFFFFF', emoji: '▲' },
  { name: 'Twilio', color: '#F22F46', emoji: '◉' },
];

const testimonials = [
  {
    id: '1',
    name: 'Sarah Mitchell',
    role: 'CTO',
    company: 'FinVault Technologies',
    text: "Akif delivered a complex banking platform in record time — flawless architecture, beautiful UI, and zero downtime after launch. Genuinely one of the best engineers I've ever worked with.",
    rating: 5,
    // Optimized Unsplash Image (150x150)
    avatar:
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=150&h=150&q=80',
    avatarAlt: 'Sarah Mitchell CTO professional headshot smiling',
    color: '#6C63FF',
  },
  {
    id: '2',
    name: 'James Okafor',
    role: 'Founder & CEO',
    company: 'PulseHealth Inc.',
    text: 'Starting from scratch, Akif built the mobile platform, backend, and designed the brand, helping the app grow rapidly within its first year. Excellent execution throughout.',
    rating: 5,
    // Optimized Unsplash Image (150x150)
    avatar:
      'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=150&h=150&q=80',
    avatarAlt: 'James Okafor CEO professional headshot in office setting',
    color: '#10D9A0',
  },
  {
    id: '3',
    name: 'Priya Sharma',
    role: 'VP Engineering',
    company: 'CloudNova Systems',
    text: 'The DevOps transformation Akif led reduced our deployment time by 80% and cut infrastructure costs by 40%. An exceptional technical leader who gets things done.',
    rating: 5,
    // Optimized Unsplash Image (150x150)
    avatar:
      'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=150&h=150&q=80',
    avatarAlt: 'Priya Sharma VP Engineering professional headshot with glasses',
    color: '#00D4FF',
  },
  {
    id: '4',
    name: 'Marcus Steele',
    role: 'Creative Director',
    company: 'NeuraSpace AI',
    text: 'Our brand identity went from generic to iconic. Akif understood our vision instantly and delivered motion graphics and design systems that set us apart in a crowded market.',
    rating: 5,
    // Optimized Unsplash Image (150x150)
    avatar:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&h=150&q=80',
    avatarAlt: 'Marcus Steele Creative Director professional headshot outdoors',
    color: '#A855F7',
  },
  {
    id: '5',
    name: 'Elena Volkov',
    role: 'Head of Product',
    company: 'TradeSphere Global',
    text: 'Akif led a team of 8 engineers across 3 time zones and delivered on every milestone. His communication, technical judgment, and design instincts are unmatched.',
    rating: 5,
    // Optimized Unsplash Image (150x150)
    avatar:
      'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=150&h=150&q=80',
    avatarAlt: 'Elena Volkov Head of Product professional headshot smiling',
    color: '#F59E0B',
  },
  {
    id: '6',
    name: 'David Chen',
    role: 'E-Commerce Director',
    company: 'LuxBrand Fashion',
    text: 'Our online revenue jumped in the first year after Akif rebuilt our e-commerce platform, improving performance and user experience. The ROI from his work speaks for itself.',
    rating: 5,
    // Optimized Unsplash Image (150x150)
    avatar:
      'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=150&h=150&q=80',
    avatarAlt: 'David Chen E-Commerce Director professional headshot in business attire',
    color: '#FF6B6B',
  },
];

const row1 = testimonials?.slice(0, 3);
const row2 = testimonials?.slice(3, 6);

export default function ClientsSection() {
  return (
    <section id="clients" className="py-16 lg:py-20 relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 20% 50%, rgba(108,99,255,0.05) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 initially-hidden animate-in">
          <div className="section-label mb-3">Social Proof</div>
          <h2
            className="text-4xl md:text-6xl font-extrabold mb-4"
            style={{ color: 'var(--text-primary)', letterSpacing: '-0.04em' }}
          >
            Trusted by <span className="gradient-text">Industry Leaders</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            From scaling startups to established companies — here&apos;s what the people I&apos;ve
            worked with say.
          </p>
        </div>

        {/* Client logos marquee */}
        <div className="relative overflow-hidden mb-20 initially-hidden animate-in">
          <div
            className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
            style={{
              background: 'linear-gradient(to right, var(--bg-primary), transparent)',
            }}
          />

          <div
            className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
            style={{
              background: 'linear-gradient(to left, var(--bg-primary), transparent)',
            }}
          />

          <div className="marquee-track">
            {[...clients, ...clients]?.map((client, i) => (
              <div
                key={`client-${i}`}
                className="flex items-center gap-3 mx-6 px-6 py-4 rounded-2xl glass-card flex-shrink-0"
                style={{
                  minWidth: '140px',
                  border: `1px solid ${client?.color}20`,
                }}
              >
                <span className="text-2xl font-extrabold" style={{ color: client?.color }}>
                  {client?.emoji}
                </span>
                <span className="font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {client?.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials — two-column masonry */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials?.map((t, i) => (
            <div
              key={t?.id}
              className="testimonial-card glass-card p-7 initially-hidden animate-in"
              style={{
                animationDelay: `${i * 0.1}s`,
                border: `1px solid ${t?.color}20`,
              }}
            >
              {/* Quote icon */}
              <div className="mb-5">
                <svg width="32" height="32" viewBox="0 0 24 24" fill={t?.color} opacity="0.4">
                  <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
                  <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" />
                </svg>
              </div>

              {/* Stars */}
              <div className="stars mb-4 text-lg">{'★'?.repeat(t?.rating)}</div>

              {/* Text */}
              <p
                className="text-base leading-relaxed mb-6"
                style={{ color: 'var(--text-secondary)' }}
              >
                &quot;{t?.text}&quot;
              </p>

              {/* Author */}
              <div
                className="flex items-center gap-4 pt-4"
                style={{ borderTop: '1px solid var(--glass-border)' }}
              >
                <div
                  className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0"
                  style={{ border: `2px solid ${t?.color}40` }}
                >
                  <img src={t?.avatar} alt={t?.avatarAlt} className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
                    {t?.name}
                  </div>
                  <div
                    className="text-xs"
                    style={{ color: t?.role.includes('VP') ? t?.color : t?.color }}
                  >
                    {t?.role}, {t?.company}
                  </div>
                </div>
                <div className="ml-auto">
                  <Icon name="CheckBadgeIcon" size={20} className="text-[#10D9A0]" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
